/**
 * +tutorial: the tutorial's own little save (not the profile, not hub prefs): whether the guided
 * first run was finished or skipped, its best rotation score, and how many runs got coaching
 * tips. localStorage `keystone.tutorial`, every access in try/catch.
 */
/** localStorage's shape (kept local so this file depends on nothing in meta/). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const TUTORIAL_PREFS_KEY = 'keystone.tutorial';

export interface TutorialPrefs {
  /** 'done' after graduating, 'skipped' after "Skip tutorial"; null = never seen. */
  status: 'done' | 'skipped' | null;
  /** Best graduation rotation score (0..100), per class id. */
  best: Record<string, number>;
  /** Runs that showed coaching tips so far (they stop at COACH_RUNS). */
  coachedRuns: number;
}

export const DEFAULT_TUTORIAL_PREFS: TutorialPrefs = { status: null, best: {}, coachedRuns: 0 };

export function sanitizeTutorialPrefs(raw: unknown): TutorialPrefs {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const best: Record<string, number> = {};
  if (o['best'] && typeof o['best'] === 'object') {
    for (const [k, v] of Object.entries(o['best'] as Record<string, unknown>)) if (typeof v === 'number' && Number.isFinite(v)) best[k] = Math.max(0, Math.min(100, Math.round(v)));
  }
  const n = o['coachedRuns'];
  return {
    status: o['status'] === 'done' || o['status'] === 'skipped' ? o['status'] : null,
    best,
    coachedRuns: typeof n === 'number' && Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0,
  };
}

export class TutorialPrefsStore {
  private value: TutorialPrefs;
  constructor(private readonly storage: StorageLike | null = defaultStorage(), private readonly key = TUTORIAL_PREFS_KEY) {
    let v = DEFAULT_TUTORIAL_PREFS;
    try {
      const raw = storage?.getItem(key);
      if (raw) v = sanitizeTutorialPrefs(JSON.parse(raw));
    } catch {
      /* defaults */
    }
    this.value = v;
  }
  get(): TutorialPrefs {
    return this.value;
  }
  set(patch: Partial<TutorialPrefs>): TutorialPrefs {
    this.value = { ...this.value, ...patch };
    try {
      this.storage?.setItem(this.key, JSON.stringify(this.value));
    } catch {
      /* memory only */
    }
    return this.value;
  }
}

function defaultStorage(): StorageLike | null {
  try {
    return (globalThis as { localStorage?: StorageLike }).localStorage ?? null;
  } catch {
    return null;
  }
}

let shared: TutorialPrefsStore | null = null;
/** The app-wide store (created on first use). */
export function tutorialPrefs(): TutorialPrefsStore {
  return (shared ??= new TutorialPrefsStore());
}

/** First launch: never finished or skipped, and no runs on the profile yet. */
export function shouldOfferTutorial(p: TutorialPrefs, profileRuns: number): boolean {
  return p.status === null && profileRuns === 0;
}
